import React, { FC, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { GuildCopperSquareIcon } from '@/atoms/Icons/GuildCopperSquareIcon'
import { Image } from '@/atoms/Image'
import { Lozenge } from '@/atoms/Lozenge'
import { Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useToggle } from '@/hooks/useToggle'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge/GuildRecommendedAge'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useDimensions } from '@/utils/ScreenUtils'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getGoalTrackerSlug } from '@/utils/goal-tracker'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { BackgroundBlurImage } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/BackgroundBlurImage'
import { DotList } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/DotList'
import { ExpandableDescription } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/ExpandableDescription'
import { TheatricalHypebot } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TheatricalHypebot'
import { TheatricalPifCta } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TheatricalPifCta'
import { TheatricalTrailer } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TheatricalTrailer'
import { TicketGoalLine } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TicketGoalLine'
import { VerticalTrailerPoster } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/VerticalTrailerPoster'
import TicketSceneTrailer from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketSceneTrailer'
import { GuildTicketsBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/GuildTicketsBanner'
import { TrailerModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/TrailerModal'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'
import { useTheatricalMetadata } from '@/views/TicketCheckoutViews/hooks/useTheatricalMetadata'
import { useTicketsContext } from '../TicketsContext'
import { ParentsGuide } from './ParentsGuide/ParentsGuide'

export const CRITIC_IMAGES = {
  VERIFIED_HOT: 'v1732750381/a209eb3767a02b08bb0045a8118a4ca4',
  POPCORN: 'v1688691818/angel-studios/misc-icons/popcorn.svg',
  TOMATO: 'v1688691818/angel-studios/misc-icons/rotten-tomatoe.svg',
  IMDB: 'v1752857312/angel-studios/misc-icons/171_Imdb_logo_logos-512',
}

interface Props {
  theatricalSlug: string
  isSceneModal?: boolean
  containerRef?: React.RefObject<HTMLDivElement | null>
  theatricalRelease: TheatricalReleaseResult
  scrollToTop: () => void
  projectGuildScore?: number | null
  className?: string
}

export const ShowtimeDetailsPanel: FC<Props> = ({
  isSceneModal,
  theatricalRelease,
  containerRef,
  projectGuildScore,
  theatricalSlug,
  scrollToTop,
  className,
}) => {
  const { t } = useTranslate('tickets')
  const { query } = useSafeRouter()
  const [isTrailerModalOpen, toggleIsTrailerModalOpen] = useToggle(false)
  const { areGuildTicketsAvailable } = useGuildTickets(theatricalSlug)
  const { innerWidth, innerHeight } = useWindowSize()
  const [isScenePlayerFullScreen, setIsScenePlayerFullScreen] = useState(!!isSceneModal)
  const contentRef = useRef<HTMLDivElement>(null)
  const { height: contentHeight } = useDimensions(contentRef)
  const sceneId = getItemOrFirstEntry(query?.sceneId)
  const { experiments } = useTicketsContext()
  const {
    verticalPoster: poster,
    horizontalPoster: horizontalPoster,
    title,
    movieRating,
    movieDuration,
    critics,
    longDescription,
    region,
    hypebotCode,
  } = theatricalRelease || {}

  const resolvedHorizontalPoster = useMemo(() => {
    // shake my head
    if (theatricalSlug === slugs.soloMio) return '/v1765303388/studio-app/catalog/2c0d50e0-008a-411e-9e8e-451c74f31eb5'
    return horizontalPoster
  }, [horizontalPoster, theatricalSlug])

  const verticalPoster = poster
  const rottenTomatoesCritic = critics?.find((critic) => critic?.company === 'Rotten Tomatoes')
  const isVerifiedHot = rottenTomatoesCritic?.certifiedFresh ?? false
  const trailerUrl = experiments?.['solo-mio-showtimes-trailer']
    ? 'https://www.youtube-nocookie.com/embed/Vy45xJxnCZM'
    : region?.trailerUrl
  const trailerThumbnail = region?.trailerThumbnail
  const selectedTracker = useMemo(() => {
    const trackerSlug = getGoalTrackerSlug(region?.releaseDate)

    return theatricalRelease?.goalTrackers?.find((tracker) => tracker?.id === trackerSlug) ?? null
  }, [region?.releaseDate, theatricalRelease?.goalTrackers])

  const metadataItems = useTheatricalMetadata({
    rating: movieRating,
    movieDuration: movieDuration,
    projectGuildScore,
    critics: critics,
    guildScoreOptions: {
      icon: <GuildCopperSquareIcon size={16} />,
      triggerTextClassName: '!photon-caption-sm md:!photon-caption-md',
    },
    iconOptions: {
      guildIconColor: 'white',
    },
  })

  const dotListItems = useMemo(() => {
    return metadataItems.map((item) => ({
      icon: item.icon,
      content: item.content,
      id: item.id,
    }))
  }, [metadataItems])

  return (
    <aside
      aria-label={t('trailerSection', 'Trailer Section')}
      className={classNames('relative lg:h-full lg:w-[476px]', className)}
    >
      {verticalPoster && <BackgroundBlurImage imageUrl={verticalPoster} />}

      <div className="relative flex h-full w-full p-4 md:grid md:grid-cols-2 md:gap-4 lg:flex lg:flex-col">
        <div ref={contentRef} className="flex w-full flex-col gap-2 rounded-[16px] md:bg-white/10 md:p-4 lg:gap-3">
          <div className={classNames('flex items-center gap-4 lg:flex-col', isSceneModal && '!flex-col')}>
            {/*mobile*/}
            {verticalPoster && trailerUrl && !isSceneModal && (
              <VerticalTrailerPoster
                isVerifiedHot={isVerifiedHot}
                className="lg:hidden"
                imageUrl={verticalPoster}
                onClick={toggleIsTrailerModalOpen}
              />
            )}
            {verticalPoster && !trailerUrl && !isSceneModal && (
              <Image
                alt="movie thumbnail"
                className="rounded-[8px] lg:hidden"
                src={verticalPoster}
                width={73}
                height={109}
                sizes="(max-width: 1023px) 73px, 0px"
                loading="lazy"
              />
            )}

            {/*desktop*/}
            {trailerThumbnail && trailerUrl && !isSceneModal && (
              <TheatricalTrailer
                className="hidden lg:block"
                imageUrl={trailerThumbnail}
                trailerUrl={trailerUrl}
                isVerifiedHot={isVerifiedHot}
              />
            )}
            {resolvedHorizontalPoster && !trailerUrl && !isSceneModal && (
              <Image
                alt="movie thumbnail"
                className="hidden rounded-[8px] lg:block"
                src={resolvedHorizontalPoster}
                width={410}
                height={410 * (9 / 16)}
                loading="lazy"
              />
            )}

            {/*scene player*/}
            {isSceneModal && (
              <TicketSceneTrailer
                isOpen={isScenePlayerFullScreen}
                setIsOpen={setIsScenePlayerFullScreen}
                scrollToTop={scrollToTop}
                containerRef={containerRef}
                sceneId={sceneId as string}
              />
            )}
            <div className="lg:w-full">
              <div className="flex flex-wrap items-center gap-2">
                <Text className="photon-title-md lg:photon-title-lg" weight="bold" color="white">
                  {title}
                </Text>

                <div className="-mt-1 flex items-baseline gap-2">
                  {movieRating && <Rating rating={movieRating} />}
                  <GuildRecommendedAge projectSlug={theatricalSlug} />
                </div>
              </div>

              <DotList className="mt-1" items={dotListItems} />

              <ParentsGuide theatricalSlug={theatricalSlug} />
              {longDescription && <ExpandableDescription className="mt-2" maxLength={100} text={longDescription} />}
            </div>
          </div>

          {selectedTracker && (
            <>
              <Divider className="md:hidden lg:block" />
              <TicketGoalLine tracker={selectedTracker} />
              <Divider className="md:hidden lg:block" />
            </>
          )}

          <TheatricalPifCta className="hidden md:flex" theatricalSlug={theatricalSlug} />
        </div>

        {areGuildTicketsAvailable && innerWidth >= 1024 && innerHeight >= 780 && (
          <GuildTicketsBanner
            shouldUseGradient
            theatricalSlug={theatricalSlug}
            projectName={title ?? ''}
            className="relative z-10 hidden shrink-0 overflow-hidden rounded-[16px] lg:block"
          />
        )}

        {hypebotCode && (innerWidth < 1024 || !areGuildTicketsAvailable) && (
          <TheatricalHypebot
            style={{ maxHeight: contentHeight }}
            className="hidden min-h-0 flex-1 md:block"
            hypebotCode={hypebotCode}
            theatricalRelease={theatricalRelease}
            theatricalSlug={theatricalSlug}
          />
        )}

        {trailerUrl && (
          <TrailerModal
            open={isTrailerModalOpen}
            onClose={() => toggleIsTrailerModalOpen(false)}
            trailerUrl={trailerUrl}
          />
        )}
      </div>
    </aside>
  )
}

const Rating = ({ rating }: { rating: string }) => {
  return (
    <Lozenge
      size="sm"
      className="md:!photon-eyebrow-sm whitespace-nowrap !bg-black/30 !text-white md:h-5 md:!rounded-[5px] md:!px-2.5 md:!py-1"
      variant="transparent"
    >
      {rating}
    </Lozenge>
  )
}
const Divider = ({ className }: { className?: string }) => (
  <div className={classNames('h-[1px] bg-white/10 w-full', className)} />
)
